import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Eye, Check, Users } from '../../../components/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import {
  fetchPathwayDetail, updatePathway, createPathway,
  addStage, updateStage, deleteStage,
  addAsset, deleteAsset, uploadAssetFile, deleteAssetFile,
  addTask, updateTask, deleteTask,
  fetchPathwayAssignments, fetchCoachRespondentsForAssignment,
  assignPathway, unassignPathway,
} from '../../../lib/pathways';
import { StageEditor } from '../../../components/pathway/StageEditor';
import { VideoEmbedModal } from '../../../components/pathway/VideoEmbedModal';
import type { PathwayDetail, StageDetail, AssetType, TaskType } from '../../../types/pathway';

export function PathwayBuilderTR() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const isNew = !id || id === 'new';

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [coachId, setCoachId] = useState<string | null>(null);
  const [pathwayId, setPathwayId] = useState<string | null>(isNew ? null : id!);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [targetProfiles, setTargetProfiles] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [stages, setStages] = useState<StageDetail[]>([]);

  const [showVideoModal, setShowVideoModal] = useState(false);
  const [activeStageIdx, setActiveStageIdx] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFileType, setPendingFileType] = useState<AssetType | null>(null);
  const [saveMessage, setSaveMessage] = useState('');

  const [showAssignments, setShowAssignments] = useState(false);
  const [assignedRespondentIds, setAssignedRespondentIds] = useState<Set<string>>(new Set());
  const [allRespondents, setAllRespondents] = useState<{ id: string; name: string; email: string }[]>([]);
  const [assignLoading, setAssignLoading] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/tr/login'); return; }
    loadData();
  }, [user, navigate, id]);

  const loadData = async () => {
    if (!user?.id) return;
    try {
      const { data: coach } = await supabase
        .from('coaches')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!coach) { navigate('/tr/coach'); return; }
      setCoachId(coach.id);

      if (!isNew && id) {
        const detail = await fetchPathwayDetail(id);
        if (detail) {
          setName(detail.name);
          setDescription(detail.description);
          setTargetProfiles(detail.targetProfiles);
          setIsPublished(detail.isPublished);
          setStages(detail.stages);
        }

        const [assignments, respondents] = await Promise.all([
          fetchPathwayAssignments(id),
          fetchCoachRespondentsForAssignment(coach.id),
        ]);
        setAssignedRespondentIds(new Set(assignments.map(a => a.respondentId)));
        setAllRespondents(respondents);
      } else {
        const respondents = await fetchCoachRespondentsForAssignment(coach.id);
        setAllRespondents(respondents);
      }
    } catch (err) {
      console.error('Error loading pathway:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!coachId) return;
    setIsSaving(true);
    setSaveMessage('');

    try {
      let currentId = pathwayId;

      if (!currentId) {
        currentId = await createPathway(coachId, { name, description, targetProfiles });
        setPathwayId(currentId);
        window.history.replaceState(null, '', `/tr/coach/pathways/${currentId}/edit`);
      } else {
        await updatePathway(currentId, { name, description, targetProfiles, isPublished });
      }

      for (let i = 0; i < stages.length; i++) {
        const stage = stages[i];
        if (stage.id.startsWith('temp-')) {
          const newId = await addStage(currentId, i, { title: stage.title, objective: stage.objective });
          if (stage.description) {
            await updateStage(newId, { description: stage.description });
          }
          stages[i] = { ...stage, id: newId, pathwayId: currentId, position: i };

          for (const asset of stage.assets) {
            if (asset.id.startsWith('temp-')) {
              const newAssetId = await addAsset(newId, {
                type: asset.type,
                title: asset.title,
                url: asset.url,
                storagePath: asset.storagePath,
                position: asset.position,
              });
              asset.id = newAssetId;
              asset.stageId = newId;
            }
          }

          for (const task of stage.tasks) {
            if (task.id.startsWith('temp-')) {
              const newTaskId = await addTask(newId, { type: task.type, prompt: task.prompt, position: task.position });
              task.id = newTaskId;
              task.stageId = newId;
            }
          }
        } else {
          await updateStage(stage.id, {
            title: stage.title,
            objective: stage.objective,
            description: stage.description,
            position: i,
          });
        }
      }

      setStages([...stages]);
      setSaveMessage('Kaydedildi');
      setTimeout(() => setSaveMessage(''), 2000);
    } catch (err) {
      console.error('Error saving pathway:', err);
      setSaveMessage('Kaydetme hatasi');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddStage = () => {
    const newStage: StageDetail = {
      id: `temp-${crypto.randomUUID()}`,
      pathwayId: pathwayId || '',
      position: stages.length,
      title: '',
      objective: '',
      description: '',
      assets: [],
      tasks: [],
    };
    setStages([...stages, newStage]);
  };

  const handleDeleteStage = async (index: number) => {
    const stage = stages[index];
    if (!stage.id.startsWith('temp-')) {
      try { await deleteStage(stage.id); } catch (err) { console.error(err); return; }
    }
    setStages(stages.filter((_, i) => i !== index));
  };

  const handleMoveStage = (index: number, direction: 'up' | 'down') => {
    const newStages = [...stages];
    const targetIdx = direction === 'up' ? index - 1 : index + 1;
    if (targetIdx < 0 || targetIdx >= newStages.length) return;
    [newStages[index], newStages[targetIdx]] = [newStages[targetIdx], newStages[index]];
    setStages(newStages);
  };

  const handleUpdateStageField = (index: number, field: string, value: string) => {
    const newStages = [...stages];
    (newStages[index] as any)[field] = value;
    setStages(newStages);
  };

  const handleAddVideo = (stageIdx: number) => {
    setActiveStageIdx(stageIdx);
    setShowVideoModal(true);
  };

  const handleVideoAdded = async (title: string, url: string) => {
    if (activeStageIdx === null) return;
    const stage = stages[activeStageIdx];

    if (!stage.id.startsWith('temp-') && pathwayId) {
      try {
        const assetId = await addAsset(stage.id, {
          type: 'video_embed',
          title,
          url,
          position: stage.assets.length,
        });
        const newStages = [...stages];
        newStages[activeStageIdx] = {
          ...stage,
          assets: [...stage.assets, {
            id: assetId,
            stageId: stage.id,
            type: 'video_embed',
            title,
            url,
            storagePath: '',
            position: stage.assets.length,
          }],
        };
        setStages(newStages);
      } catch (err) { console.error(err); }
    } else {
      const newStages = [...stages];
      newStages[activeStageIdx] = {
        ...stage,
        assets: [...stage.assets, {
          id: `temp-${crypto.randomUUID()}`,
          stageId: stage.id,
          type: 'video_embed',
          title,
          url,
          storagePath: '',
          position: stage.assets.length,
        }],
      };
      setStages(newStages);
    }
  };

  const handleUploadFile = (stageIdx: number, type: AssetType) => {
    setActiveStageIdx(stageIdx);
    setPendingFileType(type);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || activeStageIdx === null || !pendingFileType || !coachId) return;

    const stage = stages[activeStageIdx];
    try {
      const storagePath = await uploadAssetFile(coachId, file);

      if (!stage.id.startsWith('temp-') && pathwayId) {
        const assetId = await addAsset(stage.id, {
          type: pendingFileType,
          title: file.name,
          storagePath,
          position: stage.assets.length,
        });
        const newStages = [...stages];
        newStages[activeStageIdx] = {
          ...stage,
          assets: [...stage.assets, {
            id: assetId,
            stageId: stage.id,
            type: pendingFileType,
            title: file.name,
            url: '',
            storagePath,
            position: stage.assets.length,
          }],
        };
        setStages(newStages);
      } else {
        const newStages = [...stages];
        newStages[activeStageIdx] = {
          ...stage,
          assets: [...stage.assets, {
            id: `temp-${crypto.randomUUID()}`,
            stageId: stage.id,
            type: pendingFileType,
            title: file.name,
            url: '',
            storagePath,
            position: stage.assets.length,
          }],
        };
        setStages(newStages);
      }
    } catch (err) { console.error('Upload error:', err); }

    e.target.value = '';
    setPendingFileType(null);
  };

  const handleDeleteAsset = async (stageIdx: number, assetId: string, storagePath?: string) => {
    const stage = stages[stageIdx];
    try {
      if (!assetId.startsWith('temp-')) {
        await deleteAsset(assetId);
      }
      if (storagePath) {
        await deleteAssetFile(storagePath);
      }
    } catch (err) { console.error(err); }

    const newStages = [...stages];
    newStages[stageIdx] = {
      ...stage,
      assets: stage.assets.filter(a => a.id !== assetId),
    };
    setStages(newStages);
  };

  const handleAddTask = async (stageIdx: number, type: TaskType) => {
    const stage = stages[stageIdx];
    const position = stage.tasks.length;

    if (!stage.id.startsWith('temp-') && pathwayId) {
      try {
        const taskId = await addTask(stage.id, { type, prompt: '', position });
        const newStages = [...stages];
        newStages[stageIdx] = {
          ...stage,
          tasks: [...stage.tasks, { id: taskId, stageId: stage.id, type, prompt: '', position }],
        };
        setStages(newStages);
      } catch (err) { console.error(err); }
    } else {
      const newStages = [...stages];
      newStages[stageIdx] = {
        ...stage,
        tasks: [...stage.tasks, { id: `temp-${crypto.randomUUID()}`, stageId: stage.id, type, prompt: '', position }],
      };
      setStages(newStages);
    }
  };

  const handleUpdateTask = async (stageIdx: number, taskId: string, prompt: string) => {
    const stage = stages[stageIdx];
    const newStages = [...stages];
    newStages[stageIdx] = {
      ...stage,
      tasks: stage.tasks.map(t => t.id === taskId ? { ...t, prompt } : t),
    };
    setStages(newStages);

    if (!taskId.startsWith('temp-')) {
      try { await updateTask(taskId, { prompt }); } catch (err) { console.error(err); }
    }
  };

  const handleDeleteTask = async (stageIdx: number, taskId: string) => {
    const stage = stages[stageIdx];
    if (!taskId.startsWith('temp-')) {
      try { await deleteTask(taskId); } catch (err) { console.error(err); return; }
    }
    const newStages = [...stages];
    newStages[stageIdx] = {
      ...stage,
      tasks: stage.tasks.filter(t => t.id !== taskId),
    };
    setStages(newStages);
  };

  const handleTogglePublish = async () => {
    if (!pathwayId) {
      setSaveMessage('Once yolu kaydedin');
      setTimeout(() => setSaveMessage(''), 2000);
      return;
    }
    try {
      await updatePathway(pathwayId, { isPublished: !isPublished });
      setIsPublished(!isPublished);
    } catch (err) { console.error(err); }
  };

  const handleToggleAssignment = async (respondentId: string) => {
    if (!pathwayId || !coachId) return;
    setAssignLoading(true);
    try {
      if (assignedRespondentIds.has(respondentId)) {
        await unassignPathway(pathwayId, respondentId);
        setAssignedRespondentIds(prev => { const n = new Set(prev); n.delete(respondentId); return n; });
      } else {
        await assignPathway(pathwayId, respondentId, coachId);
        setAssignedRespondentIds(prev => new Set(prev).add(respondentId));
      }
    } catch (err) { console.error(err); }
    setAssignLoading(false);
  };

  if (isLoading) return <LoadingSpinner message="Yukleniyor..." />;

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="freud-heading-base text-chocolate-900">
              {isNew && !pathwayId ? 'Yeni Yol' : 'Yolu Duzenle'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {saveMessage && (
              <span className={`text-xs font-medium ${saveMessage === 'Kaydedildi' ? 'text-green-600' : 'text-warm-600'}`}>
                {saveMessage}
              </span>
            )}
            {pathwayId && (
              <button
                onClick={handleTogglePublish}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors ${
                  isPublished
                    ? 'bg-green-50 text-green-700 hover:bg-green-100'
                    : 'bg-cream-100 text-chocolate-600 hover:bg-cream-200'
                }`}
              >
                <Eye className="w-3.5 h-3.5" />
                {isPublished ? 'Yayinlandi' : 'Taslak'}
              </button>
            )}
            {pathwayId && (
              <button
                onClick={() => setShowAssignments(!showAssignments)}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-cream-100 text-chocolate-600 hover:bg-cream-200 rounded-lg transition-colors"
              >
                <Users className="w-3.5 h-3.5" />
                Ata ({assignedRespondentIds.size})
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="freud-btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {isSaving ? 'Kaydediliyor...' : (
                <>
                  <Check className="w-3.5 h-3.5" />
                  Kaydet
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="freud-card-elevated p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-chocolate-600 mb-1">Yol Adi</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="orn. Liderlik Temelleri"
                  className="w-full border border-cream-300 rounded-xl px-3.5 py-2.5 text-sm text-chocolate-800 placeholder:text-chocolate-400 focus:outline-none focus:ring-2 focus:ring-sage-300 focus:border-sage-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-chocolate-600 mb-1">Aciklama</label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Bu yol ne hakkinda? Kimin icin?"
                  rows={3}
                  className="w-full border border-cream-300 rounded-xl px-3.5 py-2.5 text-sm text-chocolate-800 placeholder:text-chocolate-400 focus:outline-none focus:ring-2 focus:ring-sage-300 focus:border-sage-400 resize-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-chocolate-600 mb-1">
                  Hedef Profiller
                  <span className="text-chocolate-400 font-normal ml-1">(istege bagli)</span>
                </label>
                <textarea
                  value={targetProfiles}
                  onChange={e => setTargetProfiles(e.target.value)}
                  placeholder="Belirli PPI profilleri veya harmonik durumlari olan danisanlar icin onerilir..."
                  rows={2}
                  className="w-full border border-cream-300 rounded-xl px-3.5 py-2.5 text-sm text-chocolate-800 placeholder:text-chocolate-400 focus:outline-none focus:ring-2 focus:ring-sage-300 focus:border-sage-400 resize-none"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="freud-heading-base text-chocolate-900">
                  Asamalar ({stages.length})
                </h2>
                <button
                  onClick={handleAddStage}
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 bg-sage-100 text-sage-700 hover:bg-sage-200 rounded-xl transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Asama Ekle
                </button>
              </div>

              {stages.length === 0 ? (
                <div className="freud-card-elevated p-8 text-center">
                  <p className="text-sm text-chocolate-500 mb-3">Henuz asama yok. Yolu olusturmaya baslamak icin ilk asamanizi ekleyin.</p>
                  <button
                    onClick={handleAddStage}
                    className="inline-flex items-center gap-1.5 text-sm px-4 py-2 bg-sage-100 text-sage-700 hover:bg-sage-200 rounded-xl transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Ilk Asamayi Ekle
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {stages.map((stage, i) => (
                    <StageEditor
                      key={stage.id}
                      stage={stage}
                      index={i}
                      totalStages={stages.length}
                      onUpdateField={(field, value) => handleUpdateStageField(i, field, value)}
                      onDelete={() => handleDeleteStage(i)}
                      onMoveUp={() => handleMoveStage(i, 'up')}
                      onMoveDown={() => handleMoveStage(i, 'down')}
                      onAddVideo={() => handleAddVideo(i)}
                      onUploadFile={(type) => handleUploadFile(i, type)}
                      onDeleteAsset={(assetId, sp) => handleDeleteAsset(i, assetId, sp)}
                      onAddTask={(type) => handleAddTask(i, type)}
                      onUpdateTask={(taskId, prompt) => handleUpdateTask(i, taskId, prompt)}
                      onDeleteTask={(taskId) => handleDeleteTask(i, taskId)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            {pathwayId && showAssignments && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="freud-card-elevated p-5"
              >
                <h3 className="text-sm font-semibold text-chocolate-900 mb-3">Danisanlara Ata</h3>
                {allRespondents.length === 0 ? (
                  <p className="text-xs text-chocolate-400">Katilimci bulunamadi.</p>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {allRespondents.map(resp => {
                      const isAssigned = assignedRespondentIds.has(resp.id);
                      return (
                        <button
                          key={resp.id}
                          onClick={() => handleToggleAssignment(resp.id)}
                          disabled={assignLoading}
                          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-cream-50 transition-colors text-left"
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            isAssigned ? 'bg-sage-500 border-sage-500' : 'border-cream-300'
                          }`}>
                            {isAssigned && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-chocolate-800 truncate">{resp.name}</p>
                            <p className="text-[10px] text-chocolate-400 truncate">{resp.email}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            )}

            <div className="freud-card-elevated p-5">
              <h3 className="text-sm font-semibold text-chocolate-900 mb-3">Hizli Ipuclari</h3>
              <ul className="text-xs text-chocolate-500 space-y-2">
                <li>Net bir ilerleme icin 3-5 asama tanimlayarak baslayin</li>
                <li>Her asamanin belirli bir ogrenme hedefi olmalidir</li>
                <li>Icerigi zenginlestirmek icin YouTube veya Vimeo videolari ekleyin</li>
                <li>Uygulamali alistirmalar icin PDF calisma kagitlari yukleyin</li>
                <li>Oz farkindiligi derinlestirmek icin yansitma sorulari ekleyin</li>
                <li>Danisanlarin baslamasi icin hazir oldugunda yayinlayin</li>
              </ul>
            </div>
          </div>
        </div>
      </main>

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".pdf,.doc,.docx,.mp3,.wav,.m4a"
        onChange={handleFileSelected}
      />

      <VideoEmbedModal
        isOpen={showVideoModal}
        onClose={() => { setShowVideoModal(false); setActiveStageIdx(null); }}
        onAdd={handleVideoAdded}
      />
    </div>
  );
}
